/**
 * Typography Component
 *
 * A flexible typography component that supports all design token colors.
 * By default, inherits color from parent. Use colorClass to specify a custom color.
 *
 * @example
 * // Using custom color class from design tokens
 * <Typography variant="h1" colorClass="text-text-primary">Heading</Typography>
 * <Typography variant="b2" colorClass="text-text-brand">Brand text</Typography>
 * <Typography variant="b3" colorClass="text-icon-brand hover:text-icon-brand-hover">
 *   Hoverable text
 * </Typography>
 *
 * @example
 * // Inherit color from parent (default behavior)
 * <button className="bg-brand text-text-over-brand">
 *   <Typography variant="b3">Button Text</Typography>
 * </button>
 *
 * @example
 * // Dynamic colors in components
 * const getTextColor = (hierarchy) => {
 *   switch (hierarchy) {
 *     case 'primary': return 'text-text-over-brand';
 *     case 'secondary': return 'text-text-brand';
 *     default: return 'text-text-primary';
 *   }
 * };
 * <Typography variant="b3" colorClass={getTextColor(buttonHierarchy)}>
 *   Dynamic Text
 * </Typography>
 */
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'src/utils/utils';

const typographyVariants = cva('tw:tracking-0', {
  variants: {
    variant: {
      h1: 'tw:text-3xl tw:leading-3xl tw:font-bold',
      h2: 'tw:text-2xl tw:leading-2xl tw:font-bold',
      h3: 'tw:text-xl tw:leading-xl tw:font-bold',
      h4: 'tw:text-lg tw:leading-lg tw:font-medium',
      h5: 'tw:text-md tw:leading-md tw:font-medium',
      h6: 'tw:text-sm tw:leading-sm tw:font-medium',
      b1: 'tw:text-md tw:leading-md tw:font-regular',
      b2: 'tw:text-bw-s-m tw:leading-bw-s-m tw:font-regular',
      b3: 'tw:text-sm tw:leading-sm tw:font-regular',
      caption:
        'tw:text-sm tw:leading-sm tw:font-bold tw:uppercase tw:tracking-1',
      label: 'tw:text-sm tw:leading-sm tw:font-medium'
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
      justify: 'text-justify'
    },
    weight: {
      regular: 'font-regular',
      medium: 'font-medium',
      bold: 'font-bold'
    }
  },
  defaultVariants: {
    variant: 'b2',
    align: 'center'
  }
});

export interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof typographyVariants> {
  asChild?: boolean;
  as?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'p'
    | 'span'
    | 'div'
    | 'label'
    | 'a';
  /**
   * Custom color class from design tokens.
   * You can pass any text color class like 'text-text-primary', 'text-icon-brand', etc.
   * If not provided, the component will inherit color from its parent.
   *
   * @example
   * colorClass="text-text-primary"
   * colorClass="text-text-brand hover:text-text-brand-hover"
   * colorClass="text-icon-error"
   */
  colorClass?: string;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      className,
      variant,
      align,
      weight,
      colorClass,
      asChild = false,
      as,
      children,
      ...props
    },
    ref
  ) => {
    // Determine the default element based on variant
    const getDefaultElement = () => {
      if (as) {
        return as;
      }
      if (asChild) {
        return undefined;
      }

      switch (variant) {
        case 'h1':
          return 'h1';
        case 'h2':
          return 'h2';
        case 'h3':
          return 'h3';
        case 'h4':
          return 'h4';
        case 'h5':
          return 'h5';
        case 'h6':
          return 'h6';
        case 'caption':
        case 'label':
          return 'span';
        case 'b1':
        case 'b2':
        case 'b3':
        default:
          return 'p';
      }
    };

    const Comp = asChild ? Slot : (getDefaultElement() as React.ElementType);

    return (
      <Comp
        className={cn(
          typographyVariants({ variant, align, weight }),
          colorClass,
          className
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
Typography.displayName = 'Typography';

export { Typography, typographyVariants };
